import React from "react";

export type StoredColumnConfig = {
  order: string[];
  hidden: string[];
};

export type TableColumnDef<T = any> = {
  key: string;
  label: string;
  sortField?: string;
  // Return `true` when the column is currently allowed in the mounting context.
  // e.g. () => isEmbedded  — closes over local state at the call site.
  contextGate?: () => boolean;
  cellClassName?: string;
  headerClassName?: string;
  // Optional extra inline JSX rendered inside the <th>, next to the label.
  // Kept intentionally light — the sort chevron is handled by the shared renderer.
  extraHeaderContent?: React.ReactNode;
  renderCell: (item: T, index: number) => React.ReactNode;
};

/**
 * Load a saved column config, migrating from an optional legacy storage key.
 * The merge with `defaultOrder` ensures:
 *   - unknown keys stored earlier are dropped
 *   - newly-added columns show up (at the end of the order)
 *   - newly-added keys listed in `defaultHidden` come in hidden, so opt-in
 *     columns don't clutter existing users' layouts on upgrade.
 */
export const loadColumnConfig = (
  storageKey: string,
  defaultOrder: string[],
  legacyKey?: string,
  defaultHidden: string[] = []
): StoredColumnConfig => {
  const hiddenSet = new Set(defaultHidden.filter((k) => defaultOrder.includes(k)));

  try {
    let raw = localStorage.getItem(storageKey);
    if (!raw && legacyKey) {
      const legacy = localStorage.getItem(legacyKey);
      if (legacy) {
        localStorage.setItem(storageKey, legacy);
        localStorage.removeItem(legacyKey);
        raw = legacy;
      }
    }
    if (!raw) {
      return {
        order: [...defaultOrder],
        hidden: Array.from(hiddenSet),
      };
    }

    const parsed = JSON.parse(raw) || {};
    const seen = new Set<string>();
    const merged: string[] = [];
    for (const k of Array.isArray(parsed.order) ? parsed.order : []) {
      if (defaultOrder.includes(k) && !seen.has(k)) {
        merged.push(k);
        seen.add(k);
      }
    }
    // Any column in defaultOrder that wasn't in the stored order is NEW.
    // Track these so we know which default-hidden keys to auto-hide on upgrade.
    const newKeys = new Set<string>();
    for (const k of defaultOrder) {
      if (!seen.has(k)) {
        merged.push(k);
        newKeys.add(k);
      }
    }

    const storedHidden = Array.isArray(parsed.hidden)
      ? parsed.hidden.filter((k: any) => defaultOrder.includes(k))
      : [];
    const hiddenMerged = new Set<string>(storedHidden);
    // Auto-hide only the NEW default-hidden keys; existing keys keep the
    // user's stored preference.
    Array.from(newKeys).forEach((k) => {
      if (hiddenSet.has(k)) hiddenMerged.add(k);
    });
    return { order: merged, hidden: Array.from(hiddenMerged) };
  } catch {
    return { order: [...defaultOrder], hidden: Array.from(hiddenSet) };
  }
};

/**
 * Given the full column defs and the saved config, return the columns that
 * should actually render, in the saved order, respecting per-column context
 * gates and hidden set.
 */
export const getVisibleColumns = <T,>(
  columns: TableColumnDef<T>[],
  config: StoredColumnConfig
): TableColumnDef<T>[] => {
  return config.order
    .map((k) => columns.find((c) => c.key === k))
    .filter((c): c is TableColumnDef<T> => !!c)
    .filter((c) => (c.contextGate ? c.contextGate() : true))
    .filter((c) => !config.hidden.includes(c.key));
};

export const configsEqual = (
  a: StoredColumnConfig,
  b: StoredColumnConfig
): boolean =>
  JSON.stringify(a.order) === JSON.stringify(b.order) &&
  JSON.stringify(a.hidden) === JSON.stringify(b.hidden);
